import { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { printKitchen } from '../../utils/printService';
import { confirm } from '../../components/ConfirmDialog';

const inputCls = 'w-full rounded-xl border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none';

export default function Breakfast() {
    const [config, setConfig] = useState({ enabled: 0, print_time: '09:00', printer_name: 'xp-metbex', last_run_date: null });
    const [ings, setIngs] = useState([]);
    const [allIngredients, setAllIngredients] = useState([]);
    const [printers, setPrinters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [addForm, setAddForm] = useState({ ingredient_id: '', qty_per_person: '', fixed_qty: false });
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [runs, setRuns] = useState([]);
    const [running, setRunning] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [cfgRes, ingRes, allIngRes, prRes, runsRes] = await Promise.all([
                api.get('/breakfast/config'),
                api.get('/breakfast/ingredients'),
                api.get('/ingredients'),
                api.get('/printers'),
                api.get('/breakfast/runs')
            ]);
            const cfg = cfgRes.data?.data || {};
            setConfig({
                enabled: Number(cfg.enabled || 0),
                print_time: String(cfg.print_time || '09:00').slice(0, 5),
                printer_name: cfg.printer_name || 'xp-metbex',
                last_run_date: cfg.last_run_date
            });
            setIngs(ingRes.data?.data || []);
            setAllIngredients(allIngRes.data?.data || []);
            setPrinters(prRes.data?.data || []);
            setRuns(runsRes.data?.data || []);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const saveConfig = async () => {
        setSaving(true);
        try {
            await api.put('/breakfast/config', {
                enabled: config.enabled ? 1 : 0,
                print_time: config.print_time,
                printer_name: config.printer_name
            });
            // Desktop app içindəsə Task Scheduler trigger saatını dərhal yenilə
            try {
                if (window.chrome?.webview?.postMessage) {
                    window.chrome.webview.postMessage({ type: 'breakfast-config-changed' });
                }
            } catch { /* ignore */ }
            toast.success('Tənzimləmə yadda saxlandı.');
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        } finally {
            setSaving(false);
        }
    };

    const addIngredient = async () => {
        if (!addForm.ingredient_id || !addForm.qty_per_person) return toast.error('İngredient və qty daxil edin.');
        try {
            await api.post('/breakfast/ingredients', {
                ingredient_id: Number(addForm.ingredient_id),
                qty_per_person: Number(addForm.qty_per_person),
                fixed_qty: addForm.fixed_qty ? 1 : 0
            });
            toast.success('Əlavə edildi.');
            setAddForm({ ingredient_id: '', qty_per_person: '', fixed_qty: false });
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        }
    };

    // fixed_qty-ni qoruyuruq — yalnız qty redaktə olunanda sıfırlanmasın
    const updateIngredient = async (id, qty, fixedQty) => {
        try {
            await api.put(`/breakfast/ingredients/${id}`, {
                qty_per_person: Number(qty),
                fixed_qty: fixedQty ? 1 : 0,
                sort_order: 0
            });
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        }
    };

    const deleteIngredient = async (id) => {
        if (!(await confirm('Silinsin?'))) return;
        try {
            await api.delete(`/breakfast/ingredients/${id}`);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        }
    };

    const loadPreview = async () => {
        setPreviewLoading(true);
        try {
            const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Baku' });
            const { data } = await api.get(`/breakfast/preview?date=${today}`);
            setPreview(data.data);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Preview alınmadı.');
        } finally {
            setPreviewLoading(false);
        }
    };

    // Çap proqramın öz körpüsü (WebView2) ilə — mətbəx/müştəri cap kimi.
    // 1) backend data hazırlayır (çap etmir), 2) hər ev WebView2 ilə çap olunur,
    // 3) backend commit edir (anbar çıxışı + log).
    const runNow = async () => {
        setRunning(true);
        try {
            const { data } = await api.get('/breakfast/run-prepare');
            const prep = data.data;
            if (!prep || !prep.tickets || prep.tickets.length === 0) {
                toast.error('Bu gün üçün səhər yeməyi alacaq ev yoxdur.');
                return;
            }

            // Hər ev üçün ayrı kupon çap et (WebView2 körpüsü)
            let printed = 0;
            const errors = [];
            for (const t of prep.tickets) {
                try {
                    await printKitchen(prep.printer, t.text);
                    printed++;
                } catch (e) {
                    errors.push(`${t.ev_adi}: ${e.message}`);
                }
            }

            if (printed === 0) {
                toast.error('Çap alınmadı: ' + (errors[0] || 'printer əlçatan deyil'));
                return;
            }

            // Çap uğurlu — anbar çıxışı + log
            await api.post('/breakfast/commit', {
                houses_count: prep.houses_count,
                total_guests: prep.total_guests
            });

            toast.success(`Çap olundu: ${printed}/${prep.houses_count} ev, ${prep.total_guests} nəfər`);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'İcra alınmadı.');
        } finally {
            setRunning(false);
        }
    };

    if (loading) {
        return <Layout><div className="py-10 text-center text-gray-400">Yüklənir...</div></Layout>;
    }

    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-5">
                <div>
                    <h1 className="text-2xl font-black text-gray-800">🍳 Səhər yeməyi avto-cap</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Qızıl Farel rezervasiyalarından sabahkı qonaqlara avtomatik səhər yeməyi kuponu çap edir və anbarı azaldır.
                    </p>
                </div>

                {/* Config */}
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h2 className="text-sm font-bold text-gray-700 mb-4">⚙️ Tənzimləmə</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <label className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50/30 p-3 cursor-pointer">
                            <input type="checkbox" checked={Boolean(config.enabled)}
                                onChange={e => setConfig({ ...config, enabled: e.target.checked ? 1 : 0 })}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600" />
                            <div>
                                <div className="text-sm font-bold text-emerald-800">Avto cap aktiv</div>
                                <div className="text-[10px] text-emerald-700/80 mt-0.5">Hər gün konfiq olunmuş saatda işlər</div>
                            </div>
                        </label>
                        <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Cap saatı (Bakı)</label>
                            <input type="time" value={config.print_time}
                                onChange={e => setConfig({ ...config, print_time: e.target.value })}
                                className={inputCls + ' font-mono'} />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Printer</label>
                            <select value={config.printer_name}
                                onChange={e => setConfig({ ...config, printer_name: e.target.value })}
                                className={inputCls}>
                                {printers.length === 0 && <option value="">Printer əlavə edin</option>}
                                {printers.map(p => (
                                    <option key={p.id} value={p.name}>{p.label} ({p.name})</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {config.last_run_date && (
                        <div className="text-[11px] text-gray-400 mb-3">Son icra: {String(config.last_run_date).slice(0, 10)}</div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <button onClick={saveConfig} disabled={saving}
                            className="rounded-xl bg-primary-600 hover:bg-primary-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
                            {saving ? '...' : '💾 Yadda saxla'}
                        </button>
                        <button onClick={loadPreview} disabled={previewLoading}
                            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                            {previewLoading ? '...' : '👁 Bugünkü preview'}
                        </button>
                        <button onClick={runNow} disabled={running}
                            className="rounded-xl bg-rose-600 hover:bg-rose-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
                            {running ? '...' : '🖨️ İndi cap et (test)'}
                        </button>
                    </div>
                </div>

                {/* Ingredients */}
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h2 className="text-sm font-bold text-gray-700 mb-4">🥚 1 nəfərlik ingredient siyahısı</h2>
                    <div className="space-y-2 mb-3">
                        {ings.length === 0 && <p className="text-xs text-gray-400 text-center py-3">Heç bir ingredient əlavə edilməyib</p>}
                        {ings.map(ing => (
                            <div key={ing.id} className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
                                <span className="flex-1 text-sm font-medium text-slate-700">{ing.ingredient_name}</span>
                                <input type="number" step="0.001" min="0"
                                    defaultValue={Number(ing.qty_per_person)}
                                    onBlur={(e) => {
                                        const v = Number(e.target.value);
                                        if (v !== Number(ing.qty_per_person)) updateIngredient(ing.id, v, ing.fixed_qty);
                                    }}
                                    className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-right font-mono text-sm" />
                                <span className="text-[11px] text-slate-400 w-10">{ing.unit || ''}</span>
                                {/* Sabit miqdar — aktivdirsə nəfər sayına vurulmur */}
                                <button
                                    type="button"
                                    onClick={() => updateIngredient(ing.id, Number(ing.qty_per_person), !Number(ing.fixed_qty))}
                                    title={Number(ing.fixed_qty)
                                        ? 'Sabit: nəfər sayından asılı deyil (klik → nəfərə görə)'
                                        : 'Nəfərə görə: nəfər sayına vurulur (klik → sabit et)'}
                                    className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold border transition-colors ${
                                        Number(ing.fixed_qty)
                                            ? 'bg-sky-100 text-sky-700 border-sky-200'
                                            : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
                                    }`}
                                >
                                    {Number(ing.fixed_qty) ? '🔒 sabit' : '× nəfər'}
                                </button>
                                <button onClick={() => deleteIngredient(ing.id)} className="text-red-400 hover:text-red-600 px-2">✕</button>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-end gap-2 pt-3 border-t border-gray-100">
                        <div className="flex-1">
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">İngredient</label>
                            <select value={addForm.ingredient_id}
                                onChange={e => setAddForm({ ...addForm, ingredient_id: e.target.value })}
                                className={inputCls}>
                                <option value="">— seçin —</option>
                                {allIngredients
                                    .filter(ai => !ings.find(i => Number(i.ingredient_id) === Number(ai.id)))
                                    .map(ai => (
                                        <option key={ai.id} value={ai.id}>{ai.name} ({ai.unit})</option>
                                    ))}
                            </select>
                        </div>
                        <div className="w-32">
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">1 nəfərə qty</label>
                            <input type="number" step="0.001" min="0"
                                value={addForm.qty_per_person}
                                onChange={e => setAddForm({ ...addForm, qty_per_person: e.target.value })}
                                placeholder="0.250"
                                className={inputCls + ' text-right font-mono'} />
                        </div>
                        <label className="flex flex-col items-center gap-1 pb-1 cursor-pointer" title="Sabit: nəfər sayına vurulmasın">
                            <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Sabit</span>
                            <input type="checkbox"
                                checked={addForm.fixed_qty}
                                onChange={e => setAddForm({ ...addForm, fixed_qty: e.target.checked })}
                                className="h-5 w-5 rounded border-gray-300 text-sky-600" />
                        </label>
                        <button onClick={addIngredient}
                            className="rounded-xl bg-primary-600 hover:bg-primary-700 px-4 py-2 text-sm font-bold text-white">
                            + Əlavə et
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                        🔒 <b>Sabit</b> = nəfər sayına vurulmur (məs. su həmişə 1×). Adi ingredient nəfərə görə vurulur (məs. süd 2 nəfər → 2×).
                    </p>
                </div>

                {/* Preview */}
                {preview && (
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                        <h2 className="text-sm font-bold text-gray-700 mb-3">👁 Preview ({preview.date})</h2>
                        <div className="mb-3 text-sm">
                            <span className="text-gray-500">Cəmi nəfər: </span>
                            <span className="font-bold text-primary-700">{preview.totalGuests}</span>
                            <span className="text-gray-400 mx-2">·</span>
                            <span className="text-gray-500">Ev sayı: </span>
                            <span className="font-bold text-primary-700">{preview.houses.length}</span>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div>
                                <h3 className="text-xs font-bold text-gray-500 uppercase mb-2">Evlər</h3>
                                <div className="space-y-1.5">
                                    {preview.houses.length === 0 && <p className="text-xs text-gray-400">Heç bir aktiv rezervasiya yoxdur</p>}
                                    {preview.houses.map(h => (
                                        <div key={h.reservation_id} className="rounded-xl bg-slate-50 px-3 py-2 text-sm">
                                            <div className="font-bold text-slate-700">{h.ev_adi}</div>
                                            <div className="text-[11px] text-slate-500">
                                                {h.qonaq_adi} · {h.guest_count} nəfər · {String(h.rezerv_tarixi).slice(0,10)} → {String(h.cixis_tarixi).slice(0,10)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="text-xs font-bold text-gray-500 uppercase mb-2">Anbar çıxışı</h3>
                                <div className="space-y-1.5">
                                    {preview.ingredients.length === 0 && <p className="text-xs text-gray-400">Heç bir ingredient təyin edilməyib</p>}
                                    {preview.ingredients.map((i, idx) => (
                                        <div key={idx} className="flex justify-between rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-sm">
                                            <span className="text-amber-800">{i.name}</span>
                                            <span className="font-mono font-bold text-amber-900">
                                                {Number(i.total).toFixed(3).replace(/\.?0+$/, '')} {i.unit || ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Runs */}
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h2 className="text-sm font-bold text-gray-700 mb-3">📋 Son icralar</h2>
                    {runs.length === 0 ? (
                        <p className="text-xs text-gray-400">Hələ heç bir icra olmayıb</p>
                    ) : (
                        <div className="overflow-hidden rounded-xl border border-gray-200">
                            <div className="overflow-x-auto"><table className="w-full text-sm min-w-[480px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-[11px] font-bold uppercase text-gray-500">Tarix</th>
                                        <th className="px-3 py-2 text-center text-[11px] font-bold uppercase text-gray-500">Ev</th>
                                        <th className="px-3 py-2 text-center text-[11px] font-bold uppercase text-gray-500">Nəfər</th>
                                        <th className="px-3 py-2 text-left text-[11px] font-bold uppercase text-gray-500">Status</th>
                                        <th className="px-3 py-2 text-left text-[11px] font-bold uppercase text-gray-500">Xəta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.map(r => (
                                        <tr key={r.id} className="border-t border-gray-100">
                                            <td className="px-3 py-2 text-gray-700 font-mono text-xs">{String(r.run_date).slice(0,10)}</td>
                                            <td className="px-3 py-2 text-center">{r.houses_count}</td>
                                            <td className="px-3 py-2 text-center">{r.guests_count}</td>
                                            <td className="px-3 py-2">
                                                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                                                    r.status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                                                    r.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                                    'bg-red-100 text-red-700'
                                                }`}>{r.status}</span>
                                            </td>
                                            <td className="px-3 py-2 text-[11px] text-red-500 truncate max-w-[300px]">{r.error_msg || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
